'use client'

import { cn } from '../../utils/cn'
import { ArrowRight } from 'lucide-react'
import { FadeIn } from '../motion/FadeIn'

interface HeroCTA {
  text: string
  href: string
}

interface CTABannerProps {
  heading: string
  subheading?: string
  primaryCTA: HeroCTA
  secondaryCTA?: HeroCTA
}

export function CTABanner({
  heading,
  subheading,
  primaryCTA,
  secondaryCTA
}: CTABannerProps) {
  return (
    <section className="relative py-24 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-r from-[var(--accent)] to-[var(--accent-hover)]" />

      {/* Grid pattern overlay */}
      <div className="absolute inset-0 grid-pattern opacity-10" />

      {/* Glow effects */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-black/10 rounded-full blur-3xl" />

      <div className="landing-container relative z-10">
        <div className="text-center max-w-3xl mx-auto">
          <FadeIn>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              {heading}
            </h2>
          </FadeIn>

          {subheading && (
            <FadeIn delay={0.1}>
              <p className="text-lg text-white/80 mb-8">
                {subheading}
              </p>
            </FadeIn>
          )}

          <FadeIn delay={0.2}>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              {(() => {
                const isPrimaryExternal = primaryCTA.href.startsWith('http')
                return (
                  <a
                    href={primaryCTA.href}
                    {...(isPrimaryExternal && { target: '_blank', rel: 'noopener noreferrer' })}
                    className={cn(
                      'inline-flex items-center justify-center gap-2',
                      'px-8 py-4 text-base font-semibold rounded-lg',
                      'bg-white hover:bg-white/90',
                      'transition-all duration-200',
                      'shadow-lg shadow-black/20'
                    )}
                    style={{ color: '#0B0B0C' }}
                  >
                    {primaryCTA.text}
                    <ArrowRight className="h-5 w-5" />
                  </a>
                )
              })()}
              {secondaryCTA && (() => {
                const isSecondaryExternal = secondaryCTA.href.startsWith('http')
                return (
                  <a
                    href={secondaryCTA.href}
                    {...(isSecondaryExternal && { target: '_blank', rel: 'noopener noreferrer' })}
                    className={cn(
                      'inline-flex items-center justify-center gap-2',
                      'px-8 py-4 text-base font-medium rounded-lg',
                      'bg-transparent border-2 border-white/30',
                      'text-white',
                      'hover:bg-white/10 hover:border-white/50',
                      'transition-all duration-200'
                    )}
                  >
                    {secondaryCTA.text}
                  </a>
                )
              })()}
            </div>
          </FadeIn>
        </div>
      </div>
    </section>
  )
}
